// src/pages/Sales/Orders/OrderReports.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import TableScrollSync from "../../../components/common/TableScrollSync";
import "./OrderReports.css";
import axios from "axios";
import toast from "../../../utils/toast";
import InvoiceDownloadModal from "../../../components/InvoiceDownloadModal/InvoiceDownloadModal";
import OrderProductsModal from "../../../components/common/OrderProductsModal";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";
import { exportToExcel, formatDateForExcel } from "../../../utils/exportToExcel";

const OrderReports = () => {
  // All orders fetched from the server (any status) — the qty search total is
  // computed against this, not just the delivered subset shown in the table.
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Order Reports");
  const [user, setUser] = useState(null);
  const [downloadingOrderId, setDownloadingOrderId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);

  // New filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [salesmanFilter, setSalesmanFilter] = useState("all");
  const [salesmen, setSalesmen] = useState([]);
  const [viewProductsOrder, setViewProductsOrder] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/login";
        return;
      }

      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || response.data);
    } catch (error) {
      console.error("Failed to load user", error);
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  // ✅ FIXED: Fetch full orders (not flattened) for salesmen
  const fetchDeliveredOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      let endpoint;
      
      // ✅ ROLE-BASED ENDPOINT SELECTION - Use regular orders endpoint
      if (user?.role === "Sales man") {
        endpoint = `${backendUrl}/api/orders/salesman-orders`;
      } else {
        // Admin sees all orders
        endpoint = `${backendUrl}/api/orders/getallorders`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAllOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [backendUrl, user]);

  const fetchSalesmen = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${backendUrl}/api/users/getAllUsers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalesmen(response.data.filter((u) => u.role === "Sales man"));
    } catch (error) {
      console.error("Error fetching salesmen:", error);
    }
  }, [backendUrl]);

  // ==================== FIXED INFINITE LOOP ====================
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (user) {
      fetchDeliveredOrders();
      if (user.role !== "Sales man") {
        fetchSalesmen();
      }
    }
  }, [user, fetchDeliveredOrders, fetchSalesmen]);
  // ============================================================

  // A product-side match: name, category, or sub-category text
  const itemMatchesSearch = useCallback(
    (item) => {
      const term = searchTerm.toLowerCase().trim();
      if (!term) return true;
      return (
        item.product?.productName?.toLowerCase().includes(term) ||
        item.product?.CategoryName?.toLowerCase().includes(term) ||
        item.product?.subCategoryName?.toLowerCase().includes(term)
      );
    },
    [searchTerm]
  );

  // Search / date / salesman matching — shared by the table (delivered orders only)
  // and the qty total (computed across ALL orders, regardless of delivery status).
  const orderMatchesFilters = useCallback(
    (order) => {
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch =
        !term ||
        order.customer?.name?.toLowerCase().includes(term) ||
        order.orderItems?.some((item) => itemMatchesSearch(item));

      let matchesDate = true;
      const orderDate = new Date(order.orderDate);
      orderDate.setHours(0, 0, 0, 0);

      if (fromDate) {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        matchesDate = matchesDate && orderDate >= start;
      }

      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && orderDate <= end;
      }

      const matchesSalesman =
        salesmanFilter === "all" ||
        order.customer?.salesman?._id === salesmanFilter ||
        order.customer?.salesman === salesmanFilter;

      return matchesSearch && matchesDate && matchesSalesman;
    },
    [searchTerm, fromDate, toDate, salesmanFilter, itemMatchesSearch]
  );

  const { entriesPerPage, invoiceSystemActive } = useAppSettings();

  // Orders that have any delivered quantity — this is what the table displays.
  // Once InvoiceCounter is deleted, orders that already have a generated
  // invoice are dropped entirely rather than just hiding their invoice number.
  const deliveredOrders = useMemo(
    () =>
      allOrders.filter((order) => {
        const hasDeliveredQty = order.orderItems?.some((item) => (item.deliveredQuantity || 0) > 0);
        if (!hasDeliveredQty) return false;
        const hasInvoice =
          !!order.invoiceNumber ||
          !!order.deliveredInvoiceNumber ||
          (order.deliveredInvoiceHistory && order.deliveredInvoiceHistory.length > 0);
        return invoiceSystemActive || !hasInvoice;
      }),
    [allOrders, invoiceSystemActive]
  );

  const filteredOrders = useMemo(
    () => deliveredOrders.filter(orderMatchesFilters),
    [deliveredOrders, orderMatchesFilters]
  );

  // Sum of DELIVERED qty for items matching the search term (product/category/sub-category),
  // computed only across the delivered orders shown in this report (not pending/undelivered qty).
  const filteredQtyTotal = useMemo(() => {
    if (!searchTerm.trim()) return null;

    let sum = 0;
    filteredOrders.forEach((order) => {
      (order.orderItems || []).forEach((item) => {
        if (itemMatchesSearch(item)) {
          sum += item.deliveredQuantity || 0;
        }
      });
    });
    return sum;
  }, [filteredOrders, searchTerm, itemMatchesSearch]);

  const downloadDeliveredInvoice = async (orderId, invoiceNumber, type = "normal") => {
    setDownloadingOrderId(orderId);
    try {
      const token = localStorage.getItem("token");
      let url = `${backendUrl}/api/orders/getdeliveredinvoice/${orderId}?type=${type}`;
      if (invoiceNumber) {
        url += `&invoiceNumber=${encodeURIComponent(invoiceNumber)}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const suffix = type === "preprinted" ? "-preprinted" : "";
      link.setAttribute("download", `delivered-invoice-${invoiceNumber || orderId}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Invoice ${invoiceNumber || ""} downloaded`);
    } catch (error) {
      console.error("Error downloading delivered invoice:", error);
      if (error.response?.status === 400) {
        toast.error("No delivered quantity available for this order");
      } else {
        toast.error("Failed to download delivered invoice");
      }
    } finally {
      setDownloadingOrderId(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setSalesmanFilter("all");
  };

  const handleExportToExcel = () => {
    const exportData = filteredOrders.map((order, index) => {
      const totalOrdered =
        order.orderItems?.reduce((sum, item) => sum + item.orderedQuantity, 0) || 0;
      const totalDelivered =
        order.orderItems?.reduce((sum, item) => sum + item.deliveredQuantity, 0) || 0;
      const pendingQty = totalOrdered - totalDelivered;
      const grandTotal = getOrderGrandTotal(order).toFixed(2);

      return {
        "No": index + 1,
        "Order ID": order.orderId || order._id,
        "Customer": order.customer?.name || "N/A",
        "Total Ordered Qty": totalOrdered,
        "Total Delivered Qty": totalDelivered,
        "Pending Qty": pendingQty,
        "Grand Total (AED)": grandTotal,
        "Delivery Partner": order.assignedTo?.username || "Not assigned",
        "Order Date": formatDateForExcel(order.orderDate),
        "Status": order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || "Pending",
      };
    });

    exportToExcel(exportData, "OrderReports", "Order Reports");
    toast.success("Excel file exported successfully");
  };

  const pagination = usePaginatedData(
    filteredOrders,
    entriesPerPage,
    `${searchTerm}|${fromDate}|${toDate}|${salesmanFilter}`
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Once the order has been packed, invoiceHistory holds the actual invoiced
  // Grand Total(s) — each already rounded to the nearest whole AED (Sub
  // Total + Round Off = Grand Total). Prefer that sum over the raw
  // order-time total so this report reflects what the customer is really
  // being charged, not the pre-rounding estimate.
  const getOrderGrandTotal = (order) =>
    order.invoiceHistory?.length > 0
      ? order.invoiceHistory.reduce((sum, h) => sum + (h.amount || 0), 0)
      : order.orderItems?.reduce((sum, item) => sum + (item.totalAmount || 0), 0) || 0;

  if (!user) {
    return <div className="order-reports-loading">Loading...</div>;
  }

  return (
    <div className="order-reports-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem={activeItem}
        onSetActiveItem={setActiveItem}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />
      <main
        className={`order-reports-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <div className="order-reports-container-wrapper">
          <div className="order-reports-container">
            <div className="order-reports-header-section">
              <h2 className="order-reports-page-title">Order Reports</h2>

              <div className="order-reports-controls-group">
                <div className="order-reports-date-group">
                  <input
                    type="date"
                    id="fromDate"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="order-reports-date-input"
                  />
                </div>

                <div className="order-reports-date-group">
                  <input
                    type="date"
                    id="toDate"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="order-reports-date-input"
                  />
                </div>

                {user?.role !== "Sales man" && (
                  <select
                    className="order-reports-salesman-filter"
                    value={salesmanFilter}
                    onChange={(e) => setSalesmanFilter(e.target.value)}
                    aria-label="Filter orders by salesman"
                  >
                    <option value="all">All Salesmen</option>
                    {salesmen.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.username}
                      </option>
                    ))}
                  </select>
                )}

                <div className="order-reports-search-container">
                  <input
                    type="text"
                    className="order-reports-search-input"
                    placeholder="Search by customer, product, category or sub-category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="order-reports-search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>

                <button
                  className="order-reports-reset-button"
                  onClick={resetFilters}
                >
                  Reset Filters
                </button>

                <button
                  className="order-reports-refresh-button"
                  onClick={fetchDeliveredOrders}
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "Refresh Data"}
                </button>

                <button
                  className="order-reports-refresh-button"
                  onClick={handleExportToExcel}
                  disabled={loading || filteredOrders.length === 0}
                  title="Export to Excel"
                >
                  Export Excel
                </button>
              </div>
            </div>

            {loading ? (
              <div className="order-reports-loading">Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="order-reports-no-data">
                No orders found
                {fromDate || toDate || searchTerm || salesmanFilter !== "all"
                  ? " matching your filters"
                  : ""}
              </div>
            ) : (
              <>
                {filteredQtyTotal !== null && (
                  <div className="order-reports-qty-summary">
                    Total Delivered Qty: <strong>{filteredQtyTotal}</strong>
                  </div>
                )}

                <TableScrollSync>
                  <div className="order-reports-table-wrapper">
                    <table className="order-reports-data-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Total Ordered Qty</th> {/* ✅ Changed */}
                          <th>Total Delivered Qty</th> {/* ✅ Changed */}
                          <th>Pending Qty</th>
                          <th>Grand Total</th> {/* ✅ Changed */}
                          <th>Order Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.map((order, index) => {
                          // ✅ Calculate totals for the ENTIRE order (not per item)
                          const totalOrdered =
                            order.orderItems?.reduce(
                              (sum, item) => sum + item.orderedQuantity,
                              0,
                            ) || 0;
                          const totalDelivered =
                            order.orderItems?.reduce(
                              (sum, item) => sum + item.deliveredQuantity,
                              0,
                            ) || 0;
                          const pendingQty = totalOrdered - totalDelivered;
                          const grandTotal = getOrderGrandTotal(order).toFixed(2);
                          const hasDelivered = totalDelivered > 0;

                          return (
                            <tr key={order._id}>
                              <td>{pagination.showingFrom + index}</td>

                              <td>
                                <button
                                  type="button"
                                  className="order-reports-orderid-link"
                                  onClick={() => setViewProductsOrder(order)}
                                  title="View ordered products"
                                >
                                  {order.orderId || order._id}
                                </button>
                              </td>

                              <td>{order.customer?.name || "N/A"}</td>

                              <td>{totalOrdered}</td>
                              <td>{totalDelivered}</td>
                              <td>{pendingQty}</td>

                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <img
                                    src={DirhamSymbol}
                                    alt="AED"
                                    width={15}
                                    height={15}
                                  />
                                  <span>{grandTotal}</span>
                                </div>
                              </td>

                              <td>{formatDate(order.orderDate)}</td>

                              <td>
                                <span
                                  className={`order-reports-status-badge order-reports-status-${order.status?.toLowerCase() || "pending"}`}
                                >
                                  {order.status?.charAt(0).toUpperCase() +
                                    order.status?.slice(1) || "Pending"}
                                </span>
                              </td>

                              <td>
                                <div className="order-reports-action-buttons">
                                  {hasDelivered && order.deliveredInvoiceHistory && order.deliveredInvoiceHistory.length > 0 ? (
                                    order.deliveredInvoiceHistory.map((inv, i) => (
                                      <button
                                        key={i}
                                        className="order-reports-invoice-button delivered"
                                        onClick={() => {
                                          setPendingInvoiceData({ orderId: order._id, invoiceNumber: inv.invoiceNumber });
                                          setShowInvoiceModal(true);
                                        }}
                                        disabled={downloadingOrderId === order._id}
                                      >
                                        {downloadingOrderId === order._id
                                          ? "Downloading..."
                                          : `🧾 ${inv.invoiceNumber}`}
                                      </button>
                                    ))
                                  ) : hasDelivered ? (
                                    <button
                                      className="order-reports-invoice-button delivered"
                                      onClick={() => {
                                        setPendingInvoiceData({ orderId: order._id, invoiceNumber: null });
                                        setShowInvoiceModal(true);
                                      }}
                                      disabled={downloadingOrderId === order._id}
                                    >
                                      {downloadingOrderId === order._id
                                        ? "Downloading..."
                                        : "Delivered Invoice"}
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>

      <InvoiceDownloadModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onSelect={(type) => {
          setShowInvoiceModal(false);
          if (pendingInvoiceData) {
            downloadDeliveredInvoice(pendingInvoiceData.orderId, pendingInvoiceData.invoiceNumber, type);
          }
        }}
      />

      {viewProductsOrder && (
        <OrderProductsModal
          order={viewProductsOrder}
          onClose={() => setViewProductsOrder(null)}
        />
      )}
    </div>
  );
};

export default OrderReports;